"use client";

import { Fragment, useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import "../landing.css";
import { useLang, type Lang } from "../i18n";
import SiteNav from "@/components/marketing/SiteNav";
import SiteFooter from "@/components/marketing/SiteFooter";
import { useAuth } from "@/contexts/AuthContext";
import { START_HREF, contactHref } from "@/lib/routes";
import { trackCta } from "@/lib/analytics";
import type { PlanKey } from "@/lib/stripe/plans";
import { TIER_FEATURES, TIER_PRICING, type TierKey } from "@/lib/stripe/tierFeatures";

/* ────────────────────────────────────────────────────────────────────────────
 *  Pricing page
 *
 *  Shape: the four purchasable tiers are in view the moment the page opens —
 *  a one-line headline, the billing toggle in the same row, then a card per
 *  tier that opens on a 5:4 image TILE carrying the name and the price (the
 *  tile is the illustration slot; public/pricing/tile-*.jpg, cut by
 *  scripts/hero/pricing-tiles.py from assets we own — drop a bespoke 800x640
 *  image at the same path to replace one). Each card shows at most six
 *  features so the row fits a 900px-tall viewport; the full matrix lives in
 *  the Compare section below, which is DERIVED from TIER_FEATURES so the two
 *  can never disagree about what a plan delivers (§Marketing claims). Below
 *  that: the small print and the closing CTA. Enterprise is a band under the
 *  grid, not a fifth column — it is a conversation, not a checkout.
 *
 *  New code uses the semantic tokens (page / surface / ink / edge / accent):
 *  the tiles' overlays are `page`-coloured and the text on them is `ink`, so
 *  the same tile reads in both themes.
 * ──────────────────────────────────────────────────────────────────────────── */

type Billing = "monthly" | "annual";

const CARD_TIERS: TierKey[] = ["free", "pass", "pro", "studio"];
const ALL_TIERS: TierKey[] = ["free", "pass", "pro", "studio", "enterprise"];
const TILE_SRC: Record<TierKey, string> = {
  free: "/pricing/tile-free.jpg",
  pass: "/pricing/tile-pass.jpg",
  pro: "/pricing/tile-pro.jpg",
  studio: "/pricing/tile-studio.jpg",
  enterprise: "/pricing/tile-enterprise.jpg",
};
const TIER_NAME: Record<TierKey, string> = {
  free: "Free",
  pass: "Buyer's Pass",
  pro: "Pro",
  studio: "Studio",
  enterprise: "Enterprise",
};
/** Features a tier inherits are stated as one line ("Everything in Free"); the matrix expands them. */
const CARRY = /^(Everything in|Alles uit)/;
/** Features shown on a card before "+N more" hands off to the matrix. */
const CARD_FEATURES = 6;

function copy(lang: Lang) {
  const nl = lang === "nl";
  return {
    headline: nl ? "Betaal voor de beslissing, niet per klik." : "Pay for the decision, not per click.",
    sub: nl
      ? "Drie scores zijn gratis. Daarna één prijs voor de weken dat je zoekt, of een plan als dit je werk is."
      : "Three scores are free. Then one price for the weeks you're searching, or a plan if this is your job.",
    monthly: nl ? "Maand" : "Monthly",
    annual: nl ? "Jaar" : "Annual",
    annualNote: nl ? "2 maanden gratis" : "2 months free",
    billingLabel: nl ? "Factureringsperiode" : "Billing period",
    popular: nl ? "Populair" : "Popular",
    perMonth: nl ? "/ maand" : "/ month",
    perYear: nl ? "/ jaar" : "/ year",
    per30: nl ? "/ 30 dagen" : "/ 30 days",
    threeScores: nl ? "drie scores" : "three scores",
    from: nl ? "vanaf" : "from",
    audience: {
      free: nl ? "Iedereen" : "Anyone",
      pass: nl ? "Kopers" : "Homebuyers",
      pro: nl ? "Makelaars & adviseurs" : "Brokers & advisors",
      studio: nl ? "Ontwikkelaars & investeerders" : "Developers & investors",
      enterprise: nl ? "REITs & instellingen" : "REITs & institutions",
    } as Record<TierKey, string>,
    quantity: {
      free: nl ? "3 scores · geen kaart · geen tijdslimiet" : "3 scores · no card · no time limit",
      pass: nl ? "Onbeperkt · 30 dagen · geen verlenging" : "Unlimited · 30 days · no auto-renew",
      pro: nl ? "Onbeperkt · 1 plek · altijd opzegbaar" : "Unlimited · 1 seat · cancel anytime",
      studio: nl ? "Onbeperkt · 3 plekken · altijd opzegbaar" : "Unlimited · 3 seats · cancel anytime",
      enterprise: nl ? "Op maat · SLA · SSO" : "Custom · SLA · SSO",
    } as Record<TierKey, string>,
    cta: {
      free: nl ? "Maak gratis account" : "Create free account",
      pass: nl ? "Koop Buyer's Pass" : "Buy Buyer's Pass",
      pro: "Start Pro",
      studio: "Start Studio",
      enterprise: nl ? "Neem contact op" : "Contact sales",
    } as Record<TierKey, string>,
    redirecting: nl ? "Doorsturen…" : "Redirecting…",
    talkFirst: nl ? "Praat eerst met ons" : "Talk to us first",
    billedYear: (total: string) => (nl ? `${total} / jaar gefactureerd` : `billed ${total} / year`),
    freeCarry: nl ? "In elk account inbegrepen" : "Included in every account",
    featureCol: nl ? "Functie" : "Feature",
    more: (n: number) => (nl ? `+${n} meer in de vergelijking` : `+${n} more in the comparison`),
    enterpriseDesc: nl
      ? "Als de hele portefeuille de vraag is: maatwerk data-integratie, white-label rapporten, SLA en een eigen accountmanager."
      : "When the entire portfolio is the question: custom data ingestion, white-label reports, an SLA and a dedicated account manager.",
    enterpriseCadence: nl ? "op maat contract" : "custom contract",
    smallPrint: nl
      ? "Prijzen in euro's. Consumenten zien bij de kassa het bedrag inclusief btw; zakelijke klanten krijgen een factuur exclusief btw."
      : "Prices in euros. Consumers see the BTW-inclusive total at checkout; business customers get an ex-VAT factuur.",
    compareEyebrow: nl ? "Vergelijk" : "Compare",
    compareHeadline: nl ? "Elk plan, functie voor functie." : "Every plan, feature by feature.",
    includedFrom: (name: string) => (nl ? `Inbegrepen vanaf ${name}` : `Included from ${name}`),
    enterpriseOnly: nl ? "Alleen Enterprise" : "Enterprise only",
    faqEyebrow: nl ? "Vragen" : "Questions",
    checkoutFailed: nl ? "Afrekenen mislukt." : "Checkout failed.",
    dismiss: nl ? "Sluiten" : "Dismiss",
  };
}

export function PricingContent() {
  const { lang } = useLang();
  const c = copy(lang);
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const [billing, setBilling] = useState<Billing>("annual");
  const annual = billing === "annual";
  const [checkoutLoading, setCheckoutLoading] = useState<PlanKey | null>(null);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  useEffect(() => {
    document.body.classList.add("landing-body");
    return () => {
      document.body.classList.remove("landing-body");
    };
  }, []);

  /**
   * The number a visitor reads on each tile, for the billing period chosen.
   * Annual plans are typeset per month with the billed total as the note —
   * "€490 / year" as the big number reads dearer than "€49 / month" although
   * it is the discount.
   */
  const price = (tier: TierKey): TierPrice => {
    switch (tier) {
      case "free":
        return { amount: TIER_PRICING.free[lang], unit: c.threeScores };
      case "pass":
        return { amount: TIER_PRICING.pass[lang], unit: c.per30 };
      case "pro":
        return annual
          ? { amount: `€${TIER_PRICING.proAnnualPerMonth[lang]}`, unit: c.perMonth, note: c.billedYear(`€${TIER_PRICING.proAnnual[lang]}`) }
          : { amount: `€${TIER_PRICING.proMonthly[lang]}`, unit: c.perMonth };
      case "studio":
        return annual
          ? { amount: `€${TIER_PRICING.studioAnnualPerMonth[lang]}`, unit: c.perMonth, note: c.billedYear(`€${TIER_PRICING.studioAnnual[lang]}`) }
          : { amount: `€${TIER_PRICING.studioMonthly[lang]}`, unit: c.perMonth };
      case "enterprise":
        return { amount: lang === "nl" ? "€12.000" : "€12,000", unit: c.perYear };
    }
  };

  /**
   * Start checkout for a paid plan. A signed-out visitor is parked at /signup
   * with a redirect back here, so they return ready to click the same CTA
   * with a valid session.
   */
  const startCheckout = async (plan: PlanKey) => {
    if (authLoading) return;
    if (!user) {
      router.push(`/signup?redirect=/pricing`);
      return;
    }
    setCheckoutError(null);
    setCheckoutLoading(plan);
    try {
      const res = await fetch("/api/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });
      const payload = await res.json();
      if (!res.ok) throw new Error(payload.error ?? "Checkout failed");
      window.location.href = payload.url;
    } catch (err) {
      setCheckoutError(err instanceof Error ? err.message : "Checkout failed");
      setCheckoutLoading(null);
    }
  };

  const proPlan: PlanKey = annual ? "pro_annual" : "pro_monthly";
  const studioPlan: PlanKey = annual ? "studio_annual" : "studio_monthly";
  const ctaFor = (tier: TierKey): TierCta => {
    switch (tier) {
      case "free":
        return { label: c.cta.free, href: START_HREF, onNavigate: () => trackCta("pricing-free") };
      case "pass":
        return { label: c.cta.pass, onClick: () => startCheckout("pass"), loading: checkoutLoading === "pass" };
      case "pro":
        return { label: c.cta.pro, onClick: () => startCheckout(proPlan), loading: checkoutLoading === proPlan };
      case "studio":
        return { label: c.cta.studio, onClick: () => startCheckout(studioPlan), loading: checkoutLoading === studioPlan };
      case "enterprise":
        return { label: c.cta.enterprise, href: contactHref(lang === "nl" ? "Enterprise-aanvraag" : "Enterprise inquiry") };
    }
  };

  /* The matrix: each tier's own promises, in tier order. A cell is included
     when the column's tier is at or above the row's tier — which is exactly
     what "Everything in <previous>" promises, so the carry lines are dropped
     rather than rendered as rows. */
  const groups = ALL_TIERS.map((tier, index) => ({
    tier,
    index,
    rows: TIER_FEATURES[tier][lang].filter((f) => !CARRY.test(f)),
  }));

  return (
    <div className="font-brand antialiased">
      <SiteNav locale={lang} active="pricing" altHref={lang === "en" ? "/nl/pricing" : "/pricing"} />

      <main>
        {/* ── Header row: one line of headline, the billing toggle beside it ── */}
        <section className="pt-20 sm:pt-24 pb-5 sm:pb-6">
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8 flex flex-col md:flex-row md:items-end md:justify-between gap-5">
            <div>
              <h1 className="font-display text-[1.9rem] sm:text-[2.4rem] font-semibold text-ink leading-[1.1] tracking-[0.005em]">
                {c.headline}
              </h1>
              <p className="mt-2 text-[0.95rem] text-ink-2 leading-[1.6] max-w-[560px]">{c.sub}</p>
            </div>
            <div
              role="group"
              aria-label={c.billingLabel}
              className="inline-flex items-center self-start rounded-full border border-edge bg-surface p-1 flex-none"
            >
              <button
                type="button"
                aria-pressed={!annual}
                onClick={() => setBilling("monthly")}
                className={`px-4 py-1.5 rounded-full text-[0.8rem] font-medium transition-colors ${
                  !annual ? "bg-ink text-page" : "text-ink-2 hover:text-ink"
                }`}
              >
                {c.monthly}
              </button>
              <button
                type="button"
                aria-pressed={annual}
                onClick={() => setBilling("annual")}
                className={`px-4 py-1.5 rounded-full text-[0.8rem] font-medium transition-colors flex items-center gap-2 ${
                  annual ? "bg-ink text-page" : "text-ink-2 hover:text-ink"
                }`}
              >
                {c.annual}
                <span
                  className={`font-data text-[0.62rem] uppercase tracking-[0.1em] ${
                    annual ? "text-page/70" : "text-accent-ink"
                  }`}
                >
                  {c.annualNote}
                </span>
              </button>
            </div>
          </div>
        </section>

        {checkoutError && (
          <div className="bg-negative/[0.06] border-y border-negative/20">
            <div className="max-w-[1120px] mx-auto px-5 sm:px-8 py-3 flex items-start justify-between gap-4">
              <p className="text-[0.9rem] text-negative/90 leading-snug">
                <span className="font-semibold mr-2">{c.checkoutFailed}</span>
                <span className="font-display italic">{checkoutError}</span>
              </p>
              <button
                type="button"
                onClick={() => setCheckoutError(null)}
                className="text-negative/70 hover:text-negative transition-colors flex-shrink-0"
                aria-label={c.dismiss}
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 3L11 11M11 3L3 11" strokeLinecap="round" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* ── The four tiers, in view on arrival. Phones swipe the row. ── */}
        <section aria-label="Plans" className="pb-8 sm:pb-10">
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
            <div
              className="flex md:grid md:grid-cols-4 gap-4 overflow-x-auto md:overflow-visible snap-x snap-mandatory md:snap-none -mx-5 px-5 sm:-mx-8 sm:px-8 md:mx-0 md:px-0 pb-3 md:pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {CARD_TIERS.map((tier, i) => {
                const list = TIER_FEATURES[tier][lang];
                const carry = list.find((f) => CARRY.test(f));
                const own = list.filter((f) => !CARRY.test(f));
                return (
                  <TierCard
                    key={tier}
                    tier={tier}
                    name={TIER_NAME[tier]}
                    audience={c.audience[tier]}
                    popular={tier === "pass" ? c.popular : undefined}
                    price={price(tier)}
                    carry={carry ?? (tier === "free" ? c.freeCarry : undefined)}
                    features={own.slice(0, CARD_FEATURES)}
                    moreLabel={own.length > CARD_FEATURES ? c.more(own.length - CARD_FEATURES) : undefined}
                    quantity={c.quantity[tier]}
                    cta={ctaFor(tier)}
                    redirecting={c.redirecting}
                    secondary={
                      tier === "studio"
                        ? { label: c.talkFirst, href: contactHref(lang === "nl" ? "Studio — vraag vooraf" : "Studio — question before buying") }
                        : undefined
                    }
                    eager={i < 2}
                  />
                );
              })}
            </div>

            {/* ── Enterprise: a band, not a column — it is a conversation ── */}
            <div className="mt-4 rounded-2xl border border-edge bg-surface p-3 sm:p-4 flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
              <div className="relative w-full md:w-[240px] aspect-[5/4] md:aspect-[5/3] rounded-xl overflow-hidden flex-none bg-surface-2">
                <Image src={TILE_SRC.enterprise} alt="" fill sizes="(min-width: 768px) 240px, 90vw" className="object-cover" />
                <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-page/90 via-page/30 to-page/10" />
                <div className="absolute top-3 left-4">
                  <h3 className="font-display text-[1.35rem] font-semibold text-ink leading-none">{TIER_NAME.enterprise}</h3>
                </div>
                <div className="absolute left-4 bottom-3 flex items-baseline gap-1.5">
                  <span className="font-data text-[0.75rem] text-ink-2">{c.from}</span>
                  <span className="font-data text-[1.4rem] font-semibold text-ink tabular-nums leading-none">{price("enterprise").amount}</span>
                  <span className="font-data text-[0.75rem] text-ink-2">{price("enterprise").unit}</span>
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[0.62rem] uppercase tracking-[0.2em] text-ink-muted font-semibold mb-1.5">
                  {c.audience.enterprise} · {c.enterpriseCadence}
                </p>
                <p className="text-[0.95rem] text-ink/85 leading-[1.6] max-w-[560px]">{c.enterpriseDesc}</p>
                <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
                  {groups[4].rows.slice(0, 4).map((f) => (
                    <li key={f} className="flex items-center gap-1.5 text-[0.8rem] text-ink-2">
                      <Check className="text-accent-ink" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
              <a
                href={ctaFor("enterprise").href}
                className="flex-none text-center text-[0.85rem] font-medium rounded-lg px-5 py-3 bg-ink text-page hover:opacity-90 transition-opacity"
              >
                {c.cta.enterprise}
              </a>
            </div>

            <p className="mt-4 text-[0.75rem] text-ink-muted leading-[1.6]">{c.smallPrint}</p>
          </div>
        </section>

        {/* ── Compare: the whole matrix, derived from TIER_FEATURES ── */}
        <section id="compare" className="py-16 sm:py-24 border-t border-edge bg-surface/40 scroll-mt-20">
          <div className="max-w-[1120px] mx-auto px-5 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-accent-ink uppercase tracking-[0.18em] mb-4">{c.compareEyebrow}</p>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-ink leading-[1.15] tracking-[0.005em] mb-8 sm:mb-10">
                {c.compareHeadline}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <div className="overflow-x-auto -mx-5 px-5 sm:mx-0 sm:px-0">
                <table className="w-full min-w-[760px] border-collapse text-[0.85rem]">
                  <thead>
                    <tr className="border-b border-edge-strong">
                      <th scope="col" className="text-left pb-3 pr-4 w-[38%]">
                        <span className="sr-only">{c.featureCol}</span>
                      </th>
                      {ALL_TIERS.map((tier) => {
                        const p = price(tier);
                        return (
                          <th key={tier} scope="col" className="text-left align-bottom pb-3 px-3">
                            <div className="font-display text-[1rem] font-semibold text-ink leading-tight">{TIER_NAME[tier]}</div>
                            <div className="font-data text-[0.7rem] text-ink-muted font-normal mt-0.5 whitespace-nowrap">
                              {tier === "enterprise" ? `${c.from} ` : ""}
                              {p.amount} {p.unit}
                            </div>
                            {p.note && <div className="font-data text-[0.62rem] text-ink-muted font-normal whitespace-nowrap">{p.note}</div>}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <Fragment key={g.tier}>
                        <tr>
                          <td colSpan={ALL_TIERS.length + 1} className="pt-6 pb-2 text-[0.66rem] uppercase tracking-[0.18em] text-ink-muted">
                            {g.tier === "enterprise" ? c.enterpriseOnly : c.includedFrom(TIER_NAME[g.tier])}
                          </td>
                        </tr>
                        {g.rows.map((row) => (
                          <tr key={row} className="border-t border-edge">
                            <td className="py-2.5 pr-4 text-ink/85 leading-snug">{row}</td>
                            {ALL_TIERS.map((tier, j) => (
                              <td key={tier} className="py-2.5 px-3">
                                {j >= g.index ? (
                                  <Check className="text-accent-ink" />
                                ) : (
                                  <span className="text-ink-muted" aria-label="not included">
                                    —
                                  </span>
                                )}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 sm:py-24 border-t border-edge">
          <div className="max-w-[820px] mx-auto px-5 sm:px-8">
            <FadeIn>
              <p className="text-[0.75rem] text-ink-muted uppercase tracking-[0.18em] mb-4">{c.faqEyebrow}</p>
            </FadeIn>
            <FadeIn delay={0.05}>
              <h2 className="font-display text-[1.75rem] sm:text-[2.25rem] font-semibold text-ink leading-[1.15] tracking-[0.005em] mb-8 sm:mb-10">
                {lang === "nl" ? (
                  <>De kleine lettertjes, <em className="italic font-normal">gewoon uitgelegd</em>.</>
                ) : (
                  <>The small print, <em className="italic font-normal">in plain Dutch</em>.</>
                )}
              </h2>
            </FadeIn>
            <div className="divide-y divide-edge border-y border-edge">
              {getFaqs(lang).map((faq, i) => (
                <FadeIn key={i} delay={0.03 * i}>
                  <FaqItem q={faq.q} a={faq.a} />
                </FadeIn>
              ))}
            </div>
          </div>
        </section>

        {/* ── Final CTA ── */}
        <section className="py-16 sm:py-24 border-t border-edge text-center">
          <div className="max-w-[720px] mx-auto px-5 sm:px-8">
            <FadeIn>
              <h2 className="font-display text-[1.9rem] sm:text-[2.6rem] font-semibold text-ink leading-[1.1] tracking-[0.005em] mb-5">
                {lang === "nl" ? (
                  <>Begin met <em className="italic font-normal text-accent-ink">drie gratis scores</em>.</>
                ) : (
                  <>Start with <em className="italic font-normal text-accent-ink">three free scores</em>.</>
                )}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1}>
              <p className="text-[1rem] text-ink-2 leading-[1.7] mb-8 max-w-[520px] mx-auto">
                {lang === "nl"
                  ? "Maak een account, zoek een pand, bekijk de score. De rest is een beslissing die je later kunt maken."
                  : "Create an account, search a property, see the score. The rest is a decision you can make later."}
              </p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <Link
                href={START_HREF}
                onClick={() => trackCta("pricing-close")}
                className="inline-flex items-center px-6 py-3 bg-accent text-on-accent text-sm font-medium rounded-lg hover:bg-accent-hover active:scale-[0.98] transition-all"
              >
                {c.cta.free}
              </Link>
            </FadeIn>
          </div>
        </section>
      </main>

      <SiteFooter locale={lang} />
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 *  Tier card — tile (name + price over the image), CTA, six features, quantity
 * ──────────────────────────────────────────────────────────────────────────── */

interface TierPrice {
  amount: string;
  unit: string;
  /** Under the price: the billed total of an annual plan. */
  note?: string;
}

interface TierCta {
  label: string;
  /** Link mode (Free → signup, Enterprise → mailto:) … */
  href?: string;
  onNavigate?: () => void;
  /** … or action mode (Buyer's Pass / Pro / Studio → POST /api/checkout). */
  onClick?: () => void | Promise<void>;
  loading?: boolean;
}

interface TierCardProps {
  tier: TierKey;
  name: string;
  audience: string;
  /** Label of the "Popular" chip; only the recommended tier passes one. */
  popular?: string;
  price: TierPrice;
  /** The inherited-features line ("Everything in Free"), shown muted above the list. */
  carry?: string;
  features: string[];
  moreLabel?: string;
  quantity: string;
  cta: TierCta;
  redirecting: string;
  secondary?: { label: string; href: string };
  /** Above the fold on every viewport: the first two tiles load eagerly. */
  eager?: boolean;
}

function TierCard({
  tier,
  name,
  audience,
  popular,
  price,
  carry,
  features,
  moreLabel,
  quantity,
  cta,
  redirecting,
  secondary,
  eager = false,
}: TierCardProps) {
  const highlight = Boolean(popular);
  const button = `block w-full text-center text-[0.85rem] font-medium rounded-lg py-3 transition-all active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed ${
    highlight ? "bg-accent text-on-accent hover:bg-accent-hover" : "bg-ink text-page hover:opacity-90"
  }`;
  return (
    <div
      className={`flex-none w-[82vw] sm:w-[340px] md:w-auto snap-center flex flex-col rounded-2xl border bg-surface p-3 sm:p-4 ${
        highlight ? "border-accent/40 shadow-[0_0_40px_-16px_rgb(var(--c-accent)/0.45)]" : "border-edge"
      }`}
    >
      {/* Tile — the image slot; name top-left, price bottom-left */}
      <div className="relative aspect-[4/3] rounded-xl overflow-hidden bg-surface-2">
        <Image
          src={TILE_SRC[tier]}
          alt=""
          fill
          sizes="(min-width: 768px) 25vw, 82vw"
          className="object-cover"
          priority={eager}
        />
        <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-page/90 via-page/30 to-page/10" />
        <div aria-hidden className="absolute inset-x-0 top-0 h-1/2 bg-gradient-to-b from-page/80 via-page/40 to-transparent" />
        <div className="absolute top-3 left-4 right-3">
          <h3 className="font-display text-[1.35rem] font-semibold text-ink leading-none">{name}</h3>
          <p className="mt-1.5 flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.18em] text-ink/80 font-semibold">
            {audience}
            {popular && (
              <span className="rounded-full bg-accent text-on-accent text-[0.58rem] tracking-[0.1em] px-1.5 py-0.5">{popular}</span>
            )}
          </p>
        </div>
        <div className="absolute left-4 bottom-3 right-3">
          <div className="flex items-baseline gap-1.5">
            <span className="font-data text-[1.6rem] font-semibold text-ink tabular-nums leading-none">{price.amount}</span>
            <span className="font-data text-[0.75rem] text-ink/80">{price.unit}</span>
          </div>
          {price.note && <p className="mt-1 font-data text-[0.62rem] text-ink/70">{price.note}</p>}
        </div>
      </div>

      {/* CTA */}
      <div className="mt-3">
        {cta.href ? (
          <Link href={cta.href} onClick={cta.onNavigate} className={button}>
            {cta.label}
          </Link>
        ) : (
          <button type="button" onClick={() => cta.onClick?.()} disabled={cta.loading} className={button}>
            {cta.loading ? redirecting : cta.label}
          </button>
        )}
      </div>

      {/* Features */}
      <div className="mt-4 px-1">
        {carry && <p className="text-[0.75rem] text-ink-muted mb-1">{carry}</p>}
        <ul className="divide-y divide-dashed divide-edge">
          {features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 py-1.5 text-[0.84rem] text-ink/85 leading-snug">
              <Check className="mt-[3px] text-accent-ink" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
        {moreLabel && (
          <a href="#compare" className="inline-block mt-2 text-[0.75rem] text-accent-ink hover:underline underline-offset-4">
            {moreLabel}
          </a>
        )}
      </div>

      {/* Bottom row: what the plan is measured in, and the human door where there is one */}
      <div className="mt-auto pt-4 px-1 flex flex-col items-start gap-1.5">
        <p className="font-data text-[0.66rem] uppercase tracking-[0.08em] text-ink-muted leading-snug">{quantity}</p>
        {secondary && (
          <a href={secondary.href} className="text-[0.72rem] text-accent-ink hover:underline underline-offset-4">
            {secondary.label}
          </a>
        )}
      </div>
    </div>
  );
}

function Check({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`w-3.5 h-3.5 flex-none ${className}`}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3.5 8.5l3 3 6-7" />
    </svg>
  );
}

/* ── FadeIn (mirrors the landing page; used below the fold only) ── */

function FadeIn({ children, className = "", delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 20 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1], delay }}
    >
      {children}
    </motion.div>
  );
}

/* ── FAQ — matches the landing accordion shape ── */

function FaqItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="py-5 sm:py-6">
      {/* Vertical padding + negative margin: the tap target is the full row. */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between text-left gap-6 group py-5 sm:py-6 -my-5 sm:-my-6"
      >
        <span className="font-display text-[1.05rem] sm:text-[1.2rem] font-medium text-ink tracking-[0.01em]">{q}</span>
        <span className={`flex-shrink-0 w-5 h-5 text-ink-muted group-hover:text-accent-ink transition-all ${open ? "rotate-45" : ""}`}>
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.25" className="w-full h-full">
            <path d="M10 3v14M3 10h14" strokeLinecap="round" />
          </svg>
        </span>
      </button>
      {open && (
        <motion.p
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          transition={{ duration: 0.3, ease: [0.22, 0.1, 0.22, 1] }}
          className="text-[0.95rem] text-ink/75 leading-[1.75] mt-3 pr-10"
        >
          {a}
        </motion.p>
      )}
    </div>
  );
}

/* ── FAQ copy ── */

function getFaqs(lang: Lang) {
  if (lang === "nl") {
    return [
      { q: "Wat gebeurt er nadat mijn Buyer's Pass van 30 dagen verloopt?",
        a: "Er wordt niets meer afgeschreven. Je account keert terug naar Free — je houdt de drie scores die je al gebruikt hebt, maar krijgt er geen nieuwe. Bewaarde panden blijven zichtbaar in alleen-lezen modus; je kunt altijd een nieuwe Pass kopen als de zoektocht doorgaat." },
      { q: "Verlengt er iets automatisch?",
        a: "De Buyer's Pass niet. Dat is een eenmalige betaling van €39, punt. Pro en Studio verlengen op de cadans die je kiest (maandelijks of jaarlijks) en je kunt altijd opzeggen — de resterende tijd van de periode blijft van jou." },
      { q: "Is btw inbegrepen?",
        a: "Nederlandse consumenten zien €39 bij de kassa, inclusief btw. Zakelijke klanten zien de prijs exclusief btw met btw apart vermeld en een volledige factuur als PDF." },
      { q: "Kan ik halverwege upgraden van Buyer's Pass naar Pro?",
        a: "Ja. De ongebruikte dagen op je Buyer's Pass worden verrekend op je eerste Pro-factuur als prorata krediet. Geen dubbele kosten." },
      { q: "Delen teamplekken bewaarde panden?",
        a: "Teamplekken staan op de Studio-roadmap en zitten nog niet in het product. Zodra ze er zijn werken ze standaard vanuit een gedeelde panden-bibliotheek, met privé-tagging als een teamlid iets privé wil houden. Tot die tijd is een Studio-abonnement één account." },
      { q: "Zijn er refunds beschikbaar?",
        a: "Binnen 14 dagen na aankoop, ja — EU consumentenrecht. Daarna kijken we per geval in goede trouw. Niemand bij PlotScore gaat discussiëren over €39." },
      { q: "Is het product daadwerkelijk live tegen deze prijzen?",
        a: "Ja — alle plannen zijn live en je kunt vandaag afrekenen. Free, Buyer's Pass, Pro en Studio zijn beschikbaar via de knoppen hierboven; Enterprise loopt via een gesprek." },
    ];
  }
  return [
    { q: "What happens after my 30-day Buyer's Pass expires?",
      a: "You stop being charged. Your account reverts to Free — you keep the three scores you already used but regain none. Saved plots stay visible in read-only mode forever; you can renew for another 30 days if the search continues." },
    { q: "Does anything auto-renew?",
      a: "The Buyer's Pass does not. It's a one-time €39 purchase, period. Pro and Studio renew on the cadence you pick (monthly or annual), and you can cancel anytime — the remaining time on the period stays yours." },
    { q: "Is VAT / BTW included?",
      a: "Dutch consumers see €39 at checkout, BTW included. Business customers see the VAT-exclusive price with BTW shown separately and a proper factuur as PDF." },
    { q: "Can I upgrade from Buyer's Pass to Pro halfway through?",
      a: "Yes. The unused days on your Buyer's Pass convert to a prorated credit on your first Pro invoice. No double-charging." },
    { q: "Do team seats share saved plots?",
      a: "Team seats are on the Studio roadmap, not in the product yet. When they ship they will work off a shared plot library by default, with private-tag support if a team member wants to keep something to themselves. Until then a Studio subscription is one account." },
    { q: "Are refunds available?",
      a: "Within 14 days of purchase, yes — EU consumer law. After that, we'll look at individual cases in good faith. Nobody at PlotScore is going to argue about €39." },
    { q: "Is the product actually live at these prices today?",
      a: "Yes — every plan is live and you can check out today. Free, Buyer's Pass, Pro, and Studio are all available through the buttons above; Enterprise is a conversation away." },
  ];
}
